import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useProspectDetail } from '../../hooks/useProspects';
import { libelleMetier } from '../../config/servicesCatalog';
import {
  CALL_OUTCOME_LABELS,
  MOTIFS_PERTE_LABELS,
  PROSPECT_STATUS,
  PROSPECT_STATUS_BADGES,
  PROSPECT_STATUS_LABELS,
  STATUTS_SELECTION,
  isTerminal,
  peutCreerDemande
} from '../../services/prospectService';
import { estOuvertMaintenant, formaterEtatOuverture, formaterPlages, DAYS, DAYS_LABELS } from '../../utils/horaires';
import CallOutcomeForm from './CallOutcomeForm';
import CreateProspectRequestModal from './CreateProspectRequestModal';
import { formaterDate } from './ProspectsList';

/**
 * Fiche prospect (§5.6) — l'écran de travail de l'agent pendant l'appel.
 *
 * **Étape 1 = consulter + qualifier.** Rien ne s'écrit à la main : ni adresse, ni résumé, ni
 * services. Tout ce qui est affiché vient du scrape et reste en lecture seule ; la seule écriture
 * possible est une issue d'appel (statut, note libre, journal).
 *
 * **Étape 2 = le bouton « Enregistrer la demande ».** Actif sur `qualifie` / `rdv` (A6) et une seule
 * fois (A5) : la modale saisit ce que le client énonce au téléphone, la callable crée le compte, la
 * demande et le lien d'activation. Les champs commerciaux restent hors de cette fiche : ils
 * appartiennent à `creation_form`, pas au prospect.
 */

const TONS_BADGES = { succes: 'badge-success', echec: 'badge-error', neutre: 'badge-ghost' };

const Bloc = ({ titre, children, actions = null }) => (
  <section className="border border-base-200 rounded-lg p-4">
    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
      <h3 className="font-semibold text-sm text-base-content/70 uppercase tracking-wide">{titre}</h3>
      {actions}
    </div>
    {children}
  </section>
);

const ProspectDetail = ({ placeId }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuthContext();
  const {
    prospect,
    isLoading,
    isError,
    error,
    permissionDenied,
    enregistrerAppel,
    enregistrerAppelEnCours,
    changerStatut,
    rouvrir
  } = useProspectDetail(placeId);

  const [detailScoreOuvert, setDetailScoreOuvert] = useState(false);
  const [descriptionOuverte, setDescriptionOuverte] = useState(false);
  const [message, setMessage] = useState('');
  const [modaleDemandeOuverte, setModaleDemandeOuverte] = useState(false);

  const retour = () => navigate('/prospection');

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-4 space-y-3 animate-pulse">
        {[0, 1, 2].map((bloc) => (
          <div key={bloc} className="h-32 bg-base-200 rounded-lg" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="alert alert-error">
          <div>
            <h3 className="font-bold">
              {permissionDenied ? 'Accès refusé par Firestore' : 'Fiche inaccessible'}
            </h3>
            <p className="text-sm">
              {permissionDenied
                ? 'Déployez la règle `prospects` depuis msd_portail, puis rechargez.'
                : error?.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="alert">
          <span>Cette fiche n’existe pas (ou plus).</span>
          <button type="button" className="btn btn-sm" onClick={retour}>
            Retour à la prospection
          </button>
        </div>
      </div>
    );
  }

  const etat = estOuvertMaintenant(prospect.openingHours);
  const badge = formaterEtatOuverture(etat);
  const terminal = isTerminal(prospect.status);
  const services = prospect.services || [];

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <button type="button" className="btn btn-ghost btn-sm" onClick={retour}>
        ← Retour à la prospection
      </button>

      {/* EN-TETE */}
      <section className="border border-base-200 rounded-lg p-4">
        <div className="flex items-start gap-4 flex-wrap">
          {prospect.google?.imageUrl && (
            <img
              src={prospect.google.imageUrl}
              alt=""
              className="w-20 h-20 object-cover rounded-lg border border-base-200"
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-bold">{prospect.name}</h1>
              <span
                className={`badge badge-sm ${PROSPECT_STATUS_BADGES[prospect.status] || 'badge-ghost'}`}
              >
                {PROSPECT_STATUS_LABELS[prospect.status] || prospect.status}
              </span>
              <span className={`badge badge-sm ${TONS_BADGES[badge.ton]}`}>
                {badge.icone} {badge.texte}
              </span>
              {prospect.google?.claimed === false && (
                <span className="badge badge-sm badge-warning">🚫 non revendiquée</span>
              )}
            </div>

            <p className="text-sm text-base-content/60 mt-1">
              {prospect.categories?.join(' · ') || 'Catégories inconnues'}
            </p>

            <div className="mt-2 flex items-center gap-3">
              <span className="text-2xl font-bold text-primary">{prospect.score ?? '—'}</span>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => setDetailScoreOuvert((ouvert) => !ouvert)}
              >
                {detailScoreOuvert ? '▾' : '▸'} détail du score
              </button>
            </div>

            {detailScoreOuvert && prospect.scoreDetail && (
              <ul className="text-xs text-base-content/70 mt-1 space-y-0.5">
                <li>Non revendiquée : {prospect.scoreDetail.notClaimed} pts</li>
                <li>Téléphone valide : {prospect.scoreDetail.phone} pts</li>
                <li>Adresse complète : {prospect.scoreDetail.address} pts</li>
                <li>Note ≥ 4,0 : {prospect.scoreDetail.rating} pts</li>
                <li>≥ 5 avis : {prospect.scoreDetail.reviews} pts</li>
                <li>Métier cible : {prospect.scoreDetail.trade} pts</li>
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* CONTACT — lecture seule. Le numéro se compose dans l'outil de démarchage, pas ici :
          le `tel:` de bureau ouvrait Skype/Teams. Pas de bouton « Itinéraire » (pas de visite). */}
      <Bloc titre="Contact">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-3 flex-wrap">
            {prospect.phone ? (
              <span className="font-mono text-base font-semibold select-all">{prospect.phone}</span>
            ) : (
              <span className="text-error">Aucun numéro exploitable</span>
            )}
            {prospect.google?.mapsUrl && (
              <a
                href={prospect.google.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-outline"
              >
                🗺️ Fiche Google
              </a>
            )}
          </div>

          {prospect.address?.isComplete ? (
            <p>{prospect.address.formatted || `${prospect.address.street}, ${prospect.address.postalCode} ${prospect.address.city}`}</p>
          ) : (
            <p className="text-warning">
              ⚠️ Adresse incomplète — elle sera complétée à l’étape 2.
              {prospect.address?.formatted ? ` (${prospect.address.formatted})` : ''}
            </p>
          )}

          {/* Fiche saisie à la main : le métier tapé n'existe nulle part ailleurs (categories vide) */}
          {(prospect.categories || []).length === 0 && Boolean(libelleMetier(prospect.targetTrade)) && (
            <p>Métier : {libelleMetier(prospect.targetTrade)}</p>
          )}

          {prospect.email && <p>Email : {prospect.email}</p>}
        </div>
      </Bloc>

      {/* ACTIVITE — lecture seule : tout vient du scrape, rien n'est saisi ici.
          Les badges « services » sont ceux détectés à l'import (E1) ; ils ne sont plus cochables. */}
      <Bloc titre="Activité">
        <div className="space-y-3 text-sm">
          {(prospect.categories || []).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {prospect.categories.map((categorie) => (
                <span key={categorie} className="badge badge-outline badge-sm">
                  {categorie}
                </span>
              ))}
            </div>
          )}

          {services.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {services.map((service) => (
                <span key={service.name} className="badge badge-outline badge-sm">
                  {service.name}
                  <span className="text-xs text-base-content/40 ml-1">({service.source})</span>
                </span>
              ))}
            </div>
          )}

          {prospect.activitySummary ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-base-content/60 font-semibold mb-1">
                Résumé
              </p>
              <p className="whitespace-pre-wrap">{prospect.activitySummary}</p>
              {prospect.summaryNeedsReview && (
                <span className="badge badge-warning badge-sm mt-1">⚠️ texte à relire</span>
              )}
            </div>
          ) : (
            <p className="text-base-content/50">
              Aucun résumé : il sera rédigé avec l’opérateur à l’étape 2.
            </p>
          )}

          <div className="flex items-center gap-3 flex-wrap text-base-content/70">
            <span>
              ⭐ {prospect.google?.rating ?? '—'}
              {typeof prospect.google?.reviewCount === 'number'
                ? ` (${prospect.google.reviewCount} avis)`
                : ''}
            </span>
            {prospect.google?.reviewsDistribution && (
              <span className="text-xs">
                {[5, 4, 3, 2, 1]
                  .map((note) => `${note}★ ${prospect.google.reviewsDistribution[note] ?? 0}`)
                  .join(' · ')}
              </span>
            )}
          </div>

          {prospect.google?.ownerDescription && (
            <div>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => setDescriptionOuverte((ouvert) => !ouvert)}
              >
                {descriptionOuverte ? '▾' : '▸'} Description Google brute
              </button>
              {descriptionOuverte && (
                <p className="text-xs text-base-content/60 mt-1 whitespace-pre-wrap">
                  {prospect.google.ownerDescription}
                </p>
              )}
            </div>
          )}

          {prospect.openingHours && (
            <div className="text-xs text-base-content/60">
              <p className="font-semibold mb-1">Horaires</p>
              <ul>
                {DAYS.map((jour) => (
                  <li key={jour}>
                    {DAYS_LABELS[jour]} : {formaterPlages(prospect.openingHours[jour])}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Bloc>

      {/* APPELS */}
      <Bloc titre={`Appels (${(prospect.calls || []).length})`}>
        {(prospect.calls || []).length === 0 ? (
          <p className="text-sm text-base-content/60 mb-3">Aucun appel enregistré.</p>
        ) : (
          <ul className="text-sm space-y-1 mb-4 max-h-48 overflow-y-auto">
            {[...(prospect.calls || [])]
              .sort((a, b) => {
                const da = a.at instanceof Date ? a.at.getTime() : 0;
                const db = b.at instanceof Date ? b.at.getTime() : 0;
                return db - da;
              })
              .map((appel, index) => (
                <li key={`${appel.at?.getTime?.() || index}-${index}`} className="border-b border-base-200 pb-1">
                  <span className="font-mono text-xs text-base-content/60">
                    {formaterDate(appel.at)}
                  </span>{' '}
                  · <span className="font-semibold">{CALL_OUTCOME_LABELS[appel.outcome] || appel.outcome}</span>
                  {appel.note ? ` — ${appel.note}` : ''}
                </li>
              ))}
          </ul>
        )}

        {/* Sur un statut terminal, le formulaire est remplacé par un bandeau explicatif :
            pas de bouton grisé qui laisse croire à un bug (§5.7). */}
        {terminal ? (
          <div className="alert text-sm">
            <span>
              Fiche en lecture seule : statut « {PROSPECT_STATUS_LABELS[prospect.status]} »
              {prospect.lostReason ? ` (${MOTIFS_PERTE_LABELS[prospect.lostReason] || prospect.lostReason})` : ''}.
              {prospect.optedOut
                ? ' Opposition RGPD : ne plus appeler, aucune réactivation possible depuis l’interface.'
                : ' Réouverture possible depuis les actions ci-dessous.'}
            </span>
          </div>
        ) : (
          <CallOutcomeForm
            prospect={prospect}
            enCours={enregistrerAppelEnCours}
            onSubmit={async (outcome, options) => {
              await enregistrerAppel({
                outcome,
                options: { ...options, uid: currentUser?.uid || null }
              });
              setMessage(`Appel enregistré : ${CALL_OUTCOME_LABELS[outcome]}.`);
            }}
          />
        )}
      </Bloc>

      {/* ACTIONS */}
      <Bloc titre="Actions">
        <div className="flex flex-wrap items-center gap-2">
          {(prospect.status === PROSPECT_STATUS.PERDU || prospect.status === PROSPECT_STATUS.EXCLU) && (
            <button
              type="button"
              className="btn btn-sm btn-warning"
              disabled={prospect.optedOut === true}
              onClick={async () => {
                // eslint-disable-next-line no-alert
                if (!window.confirm('Réactiver cette fiche dans la file d’appel ?')) return;
                await rouvrir({ uid: currentUser?.uid || null });
                setMessage('Fiche réactivée.');
              }}
            >
              ♻️ Remettre dans la file
            </button>
          )}

          {!terminal && (
            <select
              className="select select-bordered select-sm"
              value=""
              onChange={async (evenement) => {
                const statut = evenement.target.value;
                if (!statut) return;
                await changerStatut({ status: statut, uid: currentUser?.uid || null });
                setMessage(`Statut mis à jour : ${PROSPECT_STATUS_LABELS[statut]}.`);
              }}
            >
              <option value="">Changer le statut…</option>
              {STATUTS_SELECTION.filter((statut) => statut !== prospect.status).map((statut) => (
                <option key={statut} value={statut}>
                  {PROSPECT_STATUS_LABELS[statut]}
                </option>
              ))}
            </select>
          )}

          {/* L'opposition RGPD vit uniquement dans la famille D du formulaire d'appel (motif
              obligatoire + confirmation en deux temps). Pas de doublon ici. */}

          {/* Étape 2 — A5 : une fiche déjà reliée à un compte ne peut plus en créer un second ;
              le lien mène à la demande (deep-link `/creation-requests?request=<userId>`). */}
          {prospect.userId ? (
            <Link
              to={`/creation-requests?request=${prospect.userId}`}
              className="btn btn-sm btn-outline ml-auto"
              title="Demande de création reliée à cette fiche"
            >
              📄 Voir la demande
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-primary ml-auto"
              disabled={!peutCreerDemande(prospect)}
              title={
                peutCreerDemande(prospect)
                  ? 'Créer le compte client et sa demande de création assistée'
                  : 'Disponible quand la fiche est qualifiée (ou un rendez-vous planifié)'
              }
              onClick={() => setModaleDemandeOuverte(true)}
            >
              ➕ Enregistrer la demande
            </button>
          )}
        </div>

        <p className="text-xs text-base-content/50 mt-3">
          Prospect importé le {formaterDate(prospect.importedAt)} · run du{' '}
          {prospect.source?.runDate || '—'} · {prospect.callAttempts ?? 0} tentative(s) sans contact
          {prospect.nextCallAt ? ` · prochain appel le ${formaterDate(prospect.nextCallAt)}` : ''}
        </p>
      </Bloc>
      {message && (
        <div className="alert alert-success text-sm">
          <span>{message}</span>
        </div>
      )}

      {modaleDemandeOuverte && (
        <CreateProspectRequestModal
          prospect={prospect}
          onFermer={() => setModaleDemandeOuverte(false)}
          onCree={() => setMessage('Demande créée : un seul mail envoyé au client.')}
        />
      )}
    </div>
  );
};

export default ProspectDetail;
